import { LogLevel } from '@lifeway/frontend-analytics';

type EnumValues<T> = T[keyof T];

export type Logger = {
  log(message: string, ...args: unknown[]): void;
  trace(message: string, ...args: unknown[]): void;
  debug(message: string, ...args: unknown[]): void;
  info(message: string, ...args: unknown[]): void;
  warn(message: string, ...args: unknown[]): void;
  error(error: string | Error, ...args: unknown[]): void;
  assert(condition: boolean | (() => boolean), message: string, ...args: unknown[]): void;
};

export function createLogger(logLevel: EnumValues<typeof LogLevel>, consoleEnabled: boolean = true): Logger {
  function defineLogFn<T>(level: EnumValues<typeof LogLevel>, callback: T): T {
    if (logLevel > level) {
      return (() => {}) as unknown as T;
    }
    return callback;
  }

  function callConsole(logFn: Exclude<keyof Logger, 'assert'>, message: string | Error, ...args: unknown[]): void {
    if (!consoleEnabled) return;
    // eslint-disable-next-line no-console
    console[logFn](message, ...args);
  }

  return {
    log: defineLogFn(LogLevel.DEFAULT, (message: string, ...args: unknown[]) => {
      callConsole('log', message, ...args);
    }),
    trace: defineLogFn(LogLevel.DEFAULT, (message: string, ...args: unknown[]) => {
      callConsole('trace', message, ...args);
    }),
    debug: defineLogFn(LogLevel.DEBUG, (message: string, ...args: unknown[]) => {
      callConsole('debug', message, ...args);
    }),
    info: defineLogFn(LogLevel.INFO, (message: string, ...args: unknown[]) => {
      callConsole('info', message, ...args);
    }),
    warn: defineLogFn(LogLevel.WARN, (message: string, ...args: unknown[]) => {
      callConsole('warn', message, ...args);
    }),
    error: defineLogFn(LogLevel.ERROR, (message: string | Error, ...args: unknown[]) => {
      callConsole('error', message, ...args);
    }),
    assert: defineLogFn(
      LogLevel.DEFAULT,
      (condition: boolean | (() => boolean), message: string, ...args: unknown[]) => {
        if (typeof condition === 'function' ? condition() : condition) {
          return;
        }
        callConsole('error', message, ...args);
      }
    ),
  };
}

export const logger = createLogger((window.config?.logLevel as EnumValues<typeof LogLevel>) || LogLevel.WARN);
